/**
 * SleepPlanPage
 *
 * Main page for the Sleep Plan feature (/tools/sleep-plan).
 * Shows wake window timer, sleep cues, insights, and tips.
 * Integrates with Baby Care for starting/ending sleep sessions.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Moon,
  Settings,
  Loader2,
  Sun,
  Lightbulb,
  X,
  Clock,
  ChevronRight,
  BarChart3,
  Lock,
  Zap,
  ArrowUpCircle,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAppMode } from '@/hooks/useAppMode';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { useLimits } from '@/hooks/useLimits';
import { getBabyName, getBabiesFromPregnancy } from '@/lib/babyUtils';
import { useBabyCare } from '@/features/baby-care';
import { useSleepPlan } from '../hooks/useSleepPlan';
import { WakeWindowTimer } from '../components/WakeWindowTimer';
import { SleepCueCards } from '../components/SleepCueCards';
import { SleepPlanOnboarding } from '../components/SleepPlanOnboarding';
import { SleepPlanSettings } from '../components/SleepPlanSettings';
import { NightSleepBatchModal } from '../components/NightSleepBatchModal';
import { BabyCareEntryModal } from '@/features/baby-care';
import { useAuth } from '@/features/auth';
import {
  isPlausibleWakeWindow,
  MIN_CYCLES_FOR_PERSONALIZATION,
  type SleepFeedback,
} from '../lib/sleepScoring';

type EndSleepStep = 'choice' | 'cancel-confirm' | 'end-options' | 'time-picker';

const END_STEP_INDEX: Record<EndSleepStep, number> = {
  choice: 0,
  'cancel-confirm': 1,
  'end-options': 1,
  'time-picker': 2,
};

export function SleepPlanPage() {
  const { t } = useTranslation('sleepPlan');
  const { t: tBabyCare } = useTranslation('babyCare');
  const { user } = useAuth();
  const isFeatureEnabled = useFeatureEnabled('sleep_plan_enabled');
  const { appMode } = useAppMode();
  const { lastCompletedPregnancy } = usePregnancyStore();
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);
  const hasMultiple = babies.length > 1;
  const [selectedBabyIndex, setSelectedBabyIndex] = useState(0);
  const babyName = getBabyName(lastCompletedPregnancy, selectedBabyIndex);
  const { isBabyAsleep } = useBabyCare();
  const { getNumberLimit, isUnlimited } = useLimits();
  const maxChildren = getNumberLimit('sleep_plan_max_children');
  const isMaxChildrenUnlimited = isUnlimited('sleep_plan_max_children');

  const {
    isLoading,
    isPremium,
    settings,
    data,
    wakeWindowState,
    personalizedWindow,
    modeTransition,
    recentCycleCount,
    activateSleepPlan,
    deactivateSleepPlan,
    updateSettings,
    changeMode,
    refreshData,
    submitFeedback,
    activeSleepSessions,
    endSleepSession,
    cancelSleepSession,
  } = useSleepPlan(selectedBabyIndex);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSleepEntryModal, setShowSleepEntryModal] = useState(false);
  const [showNightBatchModal, setShowNightBatchModal] = useState(false);

  // End sleep modal state
  const [showEndSleepModal, setShowEndSleepModal] = useState(false);
  const [endSleepStep, setEndSleepStep] = useState<EndSleepStep>('choice');
  const [selectedTime, setSelectedTime] = useState('');
  const [isEndingSleep, setIsEndingSleep] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Live timer
  const [elapsedTime, setElapsedTime] = useState('');

  // Feature disabled
  if (!isFeatureEnabled) {
    return (
      <div className="text-center py-12">
        <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Moon className="w-7 h-7 text-white/25" />
        </div>
        <h2 className="text-white text-xl font-bold">{t('featureDisabled.name')}</h2>
        <p className="text-white/60 mt-2">{t('featureDisabled.description')}</p>
      </div>
    );
  }

  // Not in baby mode
  if (appMode !== 'baby') {
    return (
      <div className="text-center py-12">
        <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Moon className="w-7 h-7 text-white/25" />
        </div>
        <p className="text-white/60">{t('notAvailable')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Premium gate: sleep_plan_max_children = 0 means not available for free
  if (maxChildren === 0 && !isMaxChildrenUnlimited) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-white/25" />
        </div>
        <h2 className="text-white text-2xl font-bold mb-3">{t('analysis.premiumRequired')}</h2>
        <p className="text-white/70 mb-8 max-w-sm mx-auto">
          {t('analysis.premiumHint')}
        </p>
        <Link
          to="/subscription"
          className="inline-block px-8 py-3 bg-pp-orange rounded-pp text-white font-semibold"
        >
          {t('analysis.premiumUpgrade')}
        </Link>
      </div>
    );
  }

  // Not activated - show activation CTA
  if (!settings?.isActive) {
    return (
      <>
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
            <Moon className="w-10 h-10 text-white/25" />
          </div>
          <h2 className="text-white text-2xl font-bold mb-3">{t('pageTitle')}</h2>
          <p className="text-white/70 mb-8 max-w-sm mx-auto">
            {t('onboarding.step1Description')}
          </p>
          <button
            onClick={() => setShowOnboarding(true)}
            className="px-8 py-3 bg-pp-orange rounded-pp text-white font-semibold"
          >
            {t('activate')}
          </button>

          <p className="text-white/40 text-xs mt-8 max-w-xs mx-auto">
            {t('disclaimer')}
          </p>
        </div>

        {showOnboarding && (
          <SleepPlanOnboarding
            onClose={() => setShowOnboarding(false)}
            onActivate={activateSleepPlan}
            settings={settings}
          />
        )}
      </>
    );
  }

  // Active sleep plan — filter sessions for selected baby
  const babyActiveSessions = activeSleepSessions.filter(s => s.baby_index === selectedBabyIndex);
  const isBabySleeping = data?.isBabySleeping ?? babyActiveSessions.length > 0;
  const activeSession = babyActiveSessions[0];

  const startTimeFormatted = activeSession
    ? new Date(activeSession.recorded_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : '';

  const sleepDurationMinutes = activeSession
    ? Math.floor((Date.now() - new Date(activeSession.recorded_at).getTime()) / 60000)
    : 0;

  return (
    <SleepPlanPageContent
      t={t}
      tBabyCare={tBabyCare}
      babyName={babyName}
      settings={settings}
      data={data}
      wakeWindowState={wakeWindowState}
      personalizedWindow={personalizedWindow}
      modeTransition={modeTransition}
      recentCycleCount={recentCycleCount}
      isPremium={isPremium}
      isBabySleeping={isBabySleeping}
      activeSession={activeSession}
      startTimeFormatted={startTimeFormatted}
      sleepDurationMinutes={sleepDurationMinutes}
      activeSleepSessions={babyActiveSessions}
      babies={babies}
      hasMultiple={hasMultiple}
      selectedBabyIndex={selectedBabyIndex}
      setSelectedBabyIndex={setSelectedBabyIndex}
      isBabyAsleep={isBabyAsleep}
      maxChildren={maxChildren}
      isMaxChildrenUnlimited={isMaxChildrenUnlimited}
      refreshData={refreshData}
      changeMode={changeMode}
      submitFeedback={submitFeedback}
      endSleepSession={endSleepSession}
      cancelSleepSession={cancelSleepSession}
      updateSettings={updateSettings}
      deactivateSleepPlan={deactivateSleepPlan}
      showSettings={showSettings}
      setShowSettings={setShowSettings}
      showSleepEntryModal={showSleepEntryModal}
      setShowSleepEntryModal={setShowSleepEntryModal}
      showNightBatchModal={showNightBatchModal}
      setShowNightBatchModal={setShowNightBatchModal}
      showEndSleepModal={showEndSleepModal}
      setShowEndSleepModal={setShowEndSleepModal}
      endSleepStep={endSleepStep}
      setEndSleepStep={setEndSleepStep}
      selectedTime={selectedTime}
      setSelectedTime={setSelectedTime}
      isEndingSleep={isEndingSleep}
      setIsEndingSleep={setIsEndingSleep}
      isCancelling={isCancelling}
      setIsCancelling={setIsCancelling}
      elapsedTime={elapsedTime}
      setElapsedTime={setElapsedTime}
      lastCompletedPregnancy={lastCompletedPregnancy}
      user={user}
    />
  );
}

/**
 * Inner content component that allows hooks after conditional returns.
 */
function SleepPlanPageContent({
  t,
  tBabyCare,
  babyName,
  settings,
  data,
  wakeWindowState,
  personalizedWindow,
  modeTransition,
  recentCycleCount,
  isPremium,
  isBabySleeping,
  activeSession,
  startTimeFormatted,
  sleepDurationMinutes,
  activeSleepSessions,
  babies,
  hasMultiple,
  selectedBabyIndex,
  setSelectedBabyIndex,
  isBabyAsleep,
  maxChildren,
  isMaxChildrenUnlimited,
  refreshData,
  changeMode,
  submitFeedback,
  endSleepSession,
  cancelSleepSession,
  updateSettings,
  deactivateSleepPlan,
  showSettings,
  setShowSettings,
  showSleepEntryModal,
  setShowSleepEntryModal,
  showNightBatchModal,
  setShowNightBatchModal,
  showEndSleepModal,
  setShowEndSleepModal,
  endSleepStep,
  setEndSleepStep,
  selectedTime,
  setSelectedTime,
  isEndingSleep,
  setIsEndingSleep,
  isCancelling,
  setIsCancelling,
  elapsedTime,
  setElapsedTime,
  lastCompletedPregnancy,
  user,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) {
  // Live timer effect
  useEffect(() => {
    if (!isBabySleeping || !activeSession) return;

    const updateTimer = () => {
      const startTime = new Date(activeSession.recorded_at);
      const now = new Date();
      const diffMs = now.getTime() - startTime.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;

      if (hours > 0) {
        setElapsedTime(`${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
      } else {
        setElapsedTime(`${minutes}:${String(seconds).padStart(2, '0')}`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isBabySleeping, activeSession, setElapsedTime]);

  // Reset time picker when entering time-picker step
  useEffect(() => {
    if (endSleepStep === 'time-picker') {
      const now = new Date();
      setSelectedTime(now.toTimeString().slice(0, 5));
    }
  }, [endSleepStep, setSelectedTime]);

  const openEndSleepModal = () => {
    setEndSleepStep('choice');
    setShowEndSleepModal(true);
  };

  const closeEndSleepModal = () => {
    setShowEndSleepModal(false);
  };

  const handleEndSleepNow = async () => {
    if (activeSleepSessions.length === 0) return;
    setIsEndingSleep(true);
    await endSleepSession(activeSleepSessions[0].id);
    setIsEndingSleep(false);
    closeEndSleepModal();
    refreshData();
  };

  const handleEndSleepWithTime = async () => {
    if (!selectedTime || activeSleepSessions.length === 0) return;

    const today = new Date();
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);

    const startTime = new Date(activeSleepSessions[0].recorded_at);
    const now = new Date();

    if (endTime > now) {
      endTime.setDate(endTime.getDate() - 1);
    }

    if (endTime < startTime) {
      return;
    }

    setIsEndingSleep(true);
    await endSleepSession(activeSleepSessions[0].id, endTime.toISOString());
    setIsEndingSleep(false);
    closeEndSleepModal();
    refreshData();
  };

  const handleCancelSleep = async () => {
    if (activeSleepSessions.length === 0) return;
    setIsCancelling(true);
    await cancelSleepSession(activeSleepSessions[0].id);
    setIsCancelling(false);
    closeEndSleepModal();
    refreshData();
  };

  const handleSleepEntryClose = () => {
    setShowSleepEntryModal(false);
    refreshData();
  };

  const currentEndStep = END_STEP_INDEX[endSleepStep as EndSleepStep];

  // Step indicator for end-sleep modal
  const stepIndicator = (
    <div className="flex gap-1 px-4 pb-3">
      {Array.from({ length: 2 }, (_, i) => (
        <div
          key={i}
          className={`flex-1 h-1 rounded-full ${
            i < currentEndStep ? 'bg-pp-orange' : 'bg-white/20'
          }`}
        />
      ))}
    </div>
  );

  const currentMode = settings?.mode ?? 'observe';

  // Sleep feedback handler for BabyCareEntryModal integration
  const handleSleepFeedbackFromModal = async (entryId: string, feedback: SleepFeedback) => {
    const now = new Date();

    // minutesAwake ist die Differenz zum letzten erfassten Schlafende - ohne
    // jede Zeitgrenze. Wurde ein Schlaefchen nicht eingetragen, sind das
    // Stunden oder Tage. Solche Werte sind Erfassungsluecken, keine Messwerte,
    // und duerfen das persoenliche Wake Window nicht verschieben. Die
    // Bewertung der Eltern wird trotzdem gespeichert.
    const measured = data?.minutesAwake ?? null;
    const wakeWindowMinutes =
      data?.wakeWindow && isPlausibleWakeWindow(measured, data.wakeWindow)
        ? measured
        : null;

    await submitFeedback({
      careEntryId: entryId,
      feedback,
      wakeWindowMinutes,
      napDurationMinutes: 0,
      sleepStartHour: now.getHours(),
    });
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-white text-xl font-bold">{t('pageTitle')}</h1>
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-[10px] transition-colors"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Baby Selector — only for multiple babies */}
      {hasMultiple && (
        <div className="flex gap-2 mb-6">
          {babies.map((baby: { index: number; name: string }) => {
            const sleeping = isBabyAsleep(baby.index);
            const isSelected = selectedBabyIndex === baby.index;
            const isLocked = !isMaxChildrenUnlimited && baby.index >= maxChildren;
            return (
              <button
                key={baby.index}
                onClick={() => !isLocked && setSelectedBabyIndex(baby.index)}
                disabled={isLocked}
                className={`flex-1 py-2.5 rounded-[10px] font-medium transition-colors text-sm ${
                  isSelected
                    ? 'bg-pp-orange text-white'
                    : isLocked
                      ? 'bg-white/5 text-white/30 cursor-not-allowed'
                      : 'bg-white/10 text-white/70'
                }`}
              >
                {baby.name}
                {isLocked && <Lock className="w-3 h-3 inline ml-1" />}
                {!isLocked && sleeping && <Moon className="w-3 h-3 inline ml-1 text-white/60" />}
              </button>
            );
          })}
        </div>
      )}

      {/* Wake Window Timer — awake state */}
      {wakeWindowState && !isBabySleeping && (
        <div className="flex justify-center mb-6">
          <WakeWindowTimer
            state={wakeWindowState}
            babyName={babyName}
            isBabySleeping={false}
            mode={currentMode}
          />
        </div>
      )}

      {/* Sleeping circle — pp-dark ring with Moon icon */}
      {isBabySleeping && (
        <div className="flex justify-center mb-6">
          <WakeWindowTimer
            state={wakeWindowState ?? { status: 'resting' as const, minutesAwake: 0, windowRange: { minMinutes: 60, maxMinutes: 120, napsMin: 3, napsMax: 5 }, progressPercent: 0, minutesUntilWindow: 0, minutesUntilClose: 0 }}
            babyName={babyName}
            isBabySleeping={true}
            sleepDurationMinutes={sleepDurationMinutes}
            startTimeFormatted={startTimeFormatted}
            mode={currentMode}
          />
        </div>
      )}

      {/* Observe mode info text */}
      {currentMode === 'observe' && !isBabySleeping && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Moon className="w-4 h-4 text-pp-orange" />
            <h3 className="text-pp-orange font-medium text-sm">{t('mode.observe')}</h3>
          </div>
          <p className="text-white text-sm">
            {t('observeInfo', { name: babyName })}
          </p>
        </div>
      )}

      {/* No data yet */}
      {!wakeWindowState && !isBabySleeping && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Moon className="w-4 h-4 text-pp-orange" />
            <h3 className="text-pp-orange font-medium text-sm">{t('dashboard.noDataYet')}</h3>
          </div>
          <p className="text-white text-sm">{t('dashboard.noDataHint')}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-3 mb-6">
        {!isBabySleeping ? (
          <button
            onClick={() => setShowSleepEntryModal(true)}
            className="py-3 px-6 bg-pp-orange text-white rounded-pp font-semibold"
          >
            {t('dashboard.startSleep')}
          </button>
        ) : (
          <button
            onClick={openEndSleepModal}
            className="py-3 px-6 bg-pp-orange text-white rounded-pp font-semibold"
          >
            {t('dashboard.endSleep')}
          </button>
        )}

        {/* Night batch button — only show mornings (before 12:00) */}
        {new Date().getHours() < 12 && (
          <button
            onClick={() => setShowNightBatchModal(true)}
            className="py-3 px-6 bg-white/10 text-white rounded-pp font-semibold border border-white/20"
          >
            <div className="flex items-center gap-2">
              <Moon className="w-4 h-4" />
              {t('nightBatch.button')}
            </div>
          </button>
        )}
      </div>

      {/* Learn mode hint text + progress — below buttons */}
      {currentMode === 'learn' && !isBabySleeping && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-pp-orange" />
            <h3 className="text-pp-orange font-medium text-sm">{t('learnProgress.title')}</h3>
          </div>
          <p className="text-white text-sm mb-3">
            {recentCycleCount >= MIN_CYCLES_FOR_PERSONALIZATION
              ? t('learnProgress.adaptiveActive', { name: babyName })
              : t('learnInfo', { name: babyName })}
          </p>
          <div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, (recentCycleCount / MIN_CYCLES_FOR_PERSONALIZATION) * 100)}%`,
                  backgroundColor: recentCycleCount >= MIN_CYCLES_FOR_PERSONALIZATION ? '#0B9488' : '#F59E11',
                }}
              />
            </div>
            <p className="text-white/40 text-xs mt-1">
              {recentCycleCount >= MIN_CYCLES_FOR_PERSONALIZATION
                ? t('learnProgress.adaptiveHint')
                : t('learnProgress.remaining', {
                    count: Math.max(0, MIN_CYCLES_FOR_PERSONALIZATION - recentCycleCount),
                  })}
            </p>
          </div>
        </div>
      )}

      {/* Sleep Cues - only show when awake */}
      {data?.sleepCues && data.sleepCues.length > 0 && !isBabySleeping && wakeWindowState && (
        <div className="mb-6">
          <SleepCueCards
            cues={data.sleepCues}
            currentStatus={wakeWindowState.status}
            mode={currentMode}
          />
        </div>
      )}

      {/* Today's Statistics */}
      {data && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-white/25" />
            <h3 className="text-white font-medium text-sm">{t('insights.title')}</h3>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <span className="text-white text-xl font-bold">{data.todayNapCount}</span>
              <p className="text-white/50 text-xs">{t('insights.naps', { count: data.todayNapCount })}</p>
            </div>
            <div className="text-center">
              <span className="text-white text-xl font-bold">
                {Math.floor(data.todayTotalSleepMinutes / 60) > 0 ? `${Math.floor(data.todayTotalSleepMinutes / 60)}h` : ''}{data.todayTotalSleepMinutes % 60}m
              </span>
              <p className="text-white/50 text-xs">{t('analysis.totalSleep')}</p>
            </div>
            <div className="text-center">
              <span className="text-white text-xl font-bold">
                {data.avgNapDurationMinutes > 0 ? `${data.avgNapDurationMinutes}m` : '-'}
              </span>
              <p className="text-white/50 text-xs">{t('analysis.avgNap')}</p>
            </div>
          </div>

          <Link to="/tools/sleep-plan/analysis">
            <button className="w-full py-3 bg-pp-orange rounded-pp flex items-center justify-center gap-2 text-white font-semibold text-base">
              {t('insights.viewAnalysis')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      )}

      {/* Tips */}
      {data?.tips && data.tips.length > 0 && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-pp-orange" />
            <h3 className="text-pp-orange font-medium text-sm">Tipps</h3>
          </div>
          <div className="space-y-2">
            {data.tips.slice(0, 3).map((tip: string, i: number) => (
              <p key={i} className="text-white/70 text-sm">
                {t(`tips.${tip}` as never)}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Mode Upgrade Prompt — only when auto-upgrade is off */}
      {modeTransition?.recommendation && !settings?.autoUpgradeMode && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpCircle className="w-4 h-4 text-pp-teal" />
            <h3 className="text-pp-teal font-medium text-sm">{t('modeUpgrade.title')}</h3>
          </div>
          <p className="text-white text-sm mb-3">{t(modeTransition.recommendation)}</p>
          <div className="flex gap-2">
            {modeTransition.canSelectActive && currentMode !== 'active' && (
              <button
                onClick={() => changeMode('active')}
                className="flex-1 py-2.5 bg-pp-orange rounded-[10px] text-white font-medium text-sm"
              >
                {t('mode.active')}
              </button>
            )}
            {modeTransition.canSelectLearn && currentMode === 'observe' && (
              <button
                onClick={() => changeMode('learn')}
                className="flex-1 py-2.5 bg-purple-500 rounded-[10px] text-white font-medium text-sm"
              >
                {t('mode.learn')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Personalized Window Info (active mode) */}
      {currentMode === 'active' && personalizedWindow?.isPersonalized && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-green-400" />
            <h3 className="text-green-400 font-medium text-sm">{t('personalizedWindow.title')}</h3>
          </div>
          <p className="text-white text-sm">
            {t('personalizedWindow.range', {
              min: personalizedWindow.rangeMin,
              max: personalizedWindow.rangeMax,
              confidence: Math.round(personalizedWindow.confidence * 100),
            })}
          </p>
          <p className="text-white/40 text-xs mt-1">
            {t(personalizedWindow.sourceKey, { count: personalizedWindow.sourceCycleCount })}
          </p>
        </div>
      )}

      {/* Age-based fallback hint (active mode, not yet personalized) */}
      {/*
        Fortschritt und Anzeigebedingung muessen denselben Zaehler benutzen.
        Die Bedingung haengt an isPersonalized, also an recentCycleCount (den
        auswertbaren Zyklen im rollierenden Fenster). Gezaehlt wurde hier aber
        mit totalCyclesWithFeedback, dem Zaehler ueber die gesamte Laufzeit.
        Wer laengst genug Feedback gegeben hat, dessen Fenster aber gerade
        duenn ist, sah dadurch "noch -30 erforderliche Schlafphasen".
      */}
      {currentMode === 'active' && personalizedWindow && !personalizedWindow.isPersonalized && data && (
        <div className="bg-white/10 rounded-[15px] p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-pp-orange" />
            <h3 className="text-pp-orange font-medium text-sm">{t('personalizedWindow.ageBased')}</h3>
          </div>
          <div className="mt-2">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, (recentCycleCount / MIN_CYCLES_FOR_PERSONALIZATION) * 100)}%`,
                  backgroundColor: '#F59E11',
                }}
              />
            </div>
            <p className="text-white/40 text-xs mt-1">
              {t('learnProgress.remaining', {
                count: Math.max(0, MIN_CYCLES_FOR_PERSONALIZATION - recentCycleCount),
              })}
            </p>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && settings && (
        <SleepPlanSettings
          settings={settings}
          correctedAgeWeeks={data?.correctedAgeWeeks ?? 0}
          onClose={() => setShowSettings(false)}
          onUpdate={updateSettings}
          onChangeMode={changeMode}
          onDeactivate={deactivateSleepPlan}
        />
      )}

      {/* Sleep Entry Modal (Baby-Care) — with integrated feedback when sleep plan is active */}
      {showSleepEntryModal && lastCompletedPregnancy?.id && (
        <BabyCareEntryModal
          careType="sleep"
          pregnancyId={lastCompletedPregnancy.id}
          onClose={handleSleepEntryClose}
          onSleepTimerStarted={() => refreshData()}
          sleepPlanMode={currentMode}
          sleepPlanPremium={isPremium}
          sleepPlanBabyName={babyName}
          onSubmitSleepFeedback={handleSleepFeedbackFromModal}
          initialBabyIndex={selectedBabyIndex}
        />
      )}

      {/* End Sleep Modal (multi-step, same pattern as Baby-Care) */}
      {/* Night Sleep Batch Modal */}
      {showNightBatchModal && lastCompletedPregnancy?.id && user && (
        <NightSleepBatchModal
          onClose={() => setShowNightBatchModal(false)}
          onSave={async (phases) => {
            const { supabase } = await import('@/lib/supabase');
            const today = new Date();
            for (const phase of phases) {
              const [fromH, fromM] = phase.from.split(':').map(Number);
              const [toH, toM] = phase.to.split(':').map(Number);

              // Night phases: if "from" is in the evening (>= 18:00), it was yesterday
              const startDate = new Date(today);
              if (fromH >= 18) {
                startDate.setDate(startDate.getDate() - 1);
              }
              startDate.setHours(fromH, fromM, 0, 0);

              const endDate = new Date(today);
              // If "to" is before "from" hour and "from" is evening, "to" is today (already correct)
              // If both are small hours (e.g. 01:00 - 03:00), both are today
              if (toH >= 18) {
                endDate.setDate(endDate.getDate() - 1);
              }
              endDate.setHours(toH, toM, 0, 0);

              // Ensure end is after start
              if (endDate <= startDate) {
                endDate.setDate(endDate.getDate() + 1);
              }

              const { error } = await supabase.from('baby_care_entries').insert({
                user_id: user!.id,
                pregnancy_id: lastCompletedPregnancy.id,
                baby_index: selectedBabyIndex,
                care_type: 'sleep',
                recorded_at: startDate.toISOString(),
                ended_at: endDate.toISOString(),
              });
              if (error) console.error('Error inserting night sleep phase:', error);
            }
            await refreshData();
          }}
        />
      )}

      {showEndSleepModal && activeSession && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={closeEndSleepModal} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">

              {/* Step 1: Choice */}
              {endSleepStep === 'choice' && (
                <>
                  <div className="bottom-sheet-header">
                    <h2>{tBabyCare('sleepTimer.whatToDo')}</h2>
                    <button
                      onClick={closeEndSleepModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-3">
                    <button
                      onClick={() => setEndSleepStep('end-options')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Sun className="w-5 h-5 text-pp-orange" />
                        <div className="text-left">
                          <span className="text-white font-medium block">{tBabyCare('sleepTimer.wokeUp')}</span>
                          <span className="text-white/50 text-sm">{tBabyCare('sleepTimer.wokeUpDesc')}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                    </button>

                    <button
                      onClick={() => setEndSleepStep('cancel-confirm')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <X className="w-5 h-5 text-red-400" />
                        <div className="text-left">
                          <span className="text-white font-medium block">{tBabyCare('sleepTimer.cancel')}</span>
                          <span className="text-white/50 text-sm">{tBabyCare('sleepTimer.cancelDesc')}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                    </button>
                  </div>

                  <div className="bottom-sheet-footer" />
                </>
              )}

              {/* Step 2a: Cancel Confirmation */}
              {endSleepStep === 'cancel-confirm' && (
                <>
                  <div className="bottom-sheet-header">
                    <h2>{tBabyCare('sleepTimer.cancelConfirmTitle')}</h2>
                    <button
                      onClick={closeEndSleepModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content">
                    <p className="text-white/70 text-center">
                      {tBabyCare('sleepTimer.cancelConfirmDescription', { name: babyName })}
                    </p>
                    <div className="mt-4 p-3 glass-effect rounded-[10px] text-center">
                      <p className="text-white/60 text-sm">{tBabyCare('sleepTimer.sleepStarted')}</p>
                      <p className="text-white font-medium">{startTimeFormatted} {tBabyCare('sleepTimer.timeUnit')}</p>
                      <p className="text-white/60 text-sm mt-1">{tBabyCare('sleepTimer.durationLabel', { duration: elapsedTime })}</p>
                    </div>
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setEndSleepStep('choice')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {tBabyCare('sleepTimer.back')}
                    </button>
                    <button
                      onClick={handleCancelSleep}
                      disabled={isCancelling}
                      className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      {isCancelling ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        tBabyCare('sleepTimer.deleteEntry')
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Step 2b: End Options */}
              {endSleepStep === 'end-options' && (
                <>
                  <div className="bottom-sheet-header">
                    <h2>{tBabyCare('sleepTimer.wakeQuestion', { name: babyName })}</h2>
                    <button
                      onClick={closeEndSleepModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-3">
                    <button
                      onClick={handleEndSleepNow}
                      disabled={isEndingSleep}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-pp-orange" />
                        <span className="text-white font-medium">{tBabyCare('sleepTimer.now')}</span>
                      </div>
                      {isEndingSleep ? (
                        <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-white/60" />
                      )}
                    </button>

                    <button
                      onClick={() => setEndSleepStep('time-picker')}
                      className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-indigo-400" />
                        <span className="text-white font-medium">{tBabyCare('sleepTimer.chooseTime')}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60" />
                    </button>
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setEndSleepStep('choice')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {tBabyCare('sleepTimer.back')}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Time Picker */}
              {endSleepStep === 'time-picker' && (
                <>
                  <div className="bottom-sheet-header">
                    <h2>{tBabyCare('sleepTimer.chooseWakeTime')}</h2>
                    <button
                      onClick={closeEndSleepModal}
                      className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {stepIndicator}

                  <div className="bottom-sheet-content space-y-4">
                    <p className="text-white/60 text-sm text-center">
                      {tBabyCare('sleepTimer.sleptSince', { name: babyName, time: startTimeFormatted })}
                    </p>
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full p-4 bg-white/10 rounded-[15px] text-white text-center text-2xl font-mono border border-white/20 focus:border-pp-orange focus:outline-none"
                    />
                  </div>

                  <div className="bottom-sheet-footer">
                    <button
                      onClick={() => setEndSleepStep('end-options')}
                      className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                    >
                      {tBabyCare('sleepTimer.back')}
                    </button>
                    <button
                      onClick={handleEndSleepWithTime}
                      disabled={isEndingSleep || !selectedTime}
                      className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                    >
                      {isEndingSleep ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        tBabyCare('sleepTimer.confirm')
                      )}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
